import { Component, inject, Input, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { IPolymorphicControlMeta, PolymorphicControlComponent, PolymorphicControlService, PolyMorphicControlTypes, DynamicTabs, ITabConfig, ITab } from '@optima-stride-ai/optima-components';
import { IBizEntityType, IRelationshipConfiguration, RelationshipLinkType, RelationshipTargetType, BizEntityTypeCategory, RelationshipType, FieldTypes } from '@optima-stride-ai/core';
import { FormCheckComponent, FormCheckInputDirective, FormCheckLabelDirective } from '@coreui/angular';
import { IconDirective } from '@coreui/icons-angular';
import { ButtonDirective } from '@coreui/angular';
import { AppFrameworkContextService, BizEntityTypeStateStoreService, IXappCap } from '@optima-stride-ai/optima-app-framework';
import { IApp } from '@optima-stride-ai/optima-platform';
import { CommonModule } from '@angular/common';
import { BizEntityTypeCategoriesNotAvailableForSelection } from '../../strategy/biz-entity-category-filters';

@Component({
  selector: 'app-form-relationship-configuration',
  imports: [
    CommonModule,
    PolymorphicControlComponent,
    FormCheckComponent,
    FormCheckInputDirective,
    FormCheckLabelDirective,
    IconDirective,
    ButtonDirective,
    DynamicTabs
],
  templateUrl: './form-relationship-configuration.component.html',
  styleUrl: './form-relationship-configuration.component.sass',
  standalone: true
})
export class FormRelationshipConfigurationComponent implements OnInit {
  @Input() relationshipConfig!: IRelationshipConfiguration | IRelationshipConfiguration[];
  @Input() isEditing: boolean = false;
  @Input() bizEntityType!: IBizEntityType;
  @Input() relationshipId?: string;
  @Input() bizEntityTypes: IBizEntityType[] = [];
  
  @ViewChild('targetControlComponent') targetControlComponent?: PolymorphicControlComponent;
  
  // Internal property to hold the actual config (handling array case)
  private _config!: IRelationshipConfiguration;
  private bizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService)
  xAppCapTabConfig: ITabConfig = {
    tabs: [],
    activeTabId:'',
    showAddButton: false,
    showSaveButton: false,
  }
  // Cardinality validation error tracking
  isCardinalityInvalid: boolean = false;
  cardinalityErrorMessage: string = '';

  private appFrameworkContextService = inject(AppFrameworkContextService);

  private appsBizEntityTypes: { [appId: string]: IBizEntityType[] } = {};
  private currentAppId: string = '';
  private availableApps: IApp[] = [];
  // Public getter to access the updated configuration (used by modal)
  get data(): { data: IRelationshipConfiguration, isRelationConfigValid: boolean } {
    
    // Update view tab title
    if (!this._config.view) {
      this._config.view = { tabTitle: '' };
    }
    this._config.view.tabTitle = this.polyTabTitleControl.value || '';
    
    // Update cardinality values
    this._config.cardinalityMax = Number(this.polyMaximumControl.value);
    this._config.cardinalityMin = Number(this.polyMinimumControl.value);
    
    // Check cardinality validation
    this.checkCardinalityValidation();
    
    // Update target - convert ID back to actual entity or category
    const selectedId = this.polyTargetControl.value;
    if (selectedId) {
      // Default to BizEntityType if targetType is not set
      const targetType = this._config.targetType ?? RelationshipTargetType.BizEntityType;
      
      if (targetType === RelationshipTargetType.BizEntityType) {
        // First try to find in the active tab's cache
        let selectedType = this.appsBizEntityTypes[this.xAppCapTabConfig.activeTabId]?.find(t => t.id.toString() === selectedId);
        
        // If not found in active tab, search across ALL cached apps
        if (!selectedType) {
          for (const appId of Object.keys(this.appsBizEntityTypes)) {
            selectedType = this.appsBizEntityTypes[appId]?.find(t => t.id.toString() === selectedId);
            if (selectedType) {
              break;
            }
          }
        }
        
        // console.log('Mapping selected target ID to entity:', selectedId, selectedType);
        if (selectedType) {
          this._config.target = selectedType;
          if(selectedType.category === BizEntityTypeCategory.Transactional){
            this._config.relationshipType = RelationshipType.Transactional;
          }
        } else {
          // Fallback: Check if existing target is already a valid IBizEntityType with matching id
          const existingTarget = this._config.target as IBizEntityType;
          if (existingTarget?.id && existingTarget?.name && existingTarget.id.toString() === selectedId) {
            // Keep existing valid target - no action needed
          } else {
            console.warn('Target entity not found in cache for id:', selectedId, '. Ensure biz entity types are loaded before saving.');
          }
        }
      } else {
        // It's a BizEntityTypeCategory enum
        this._config.target = parseInt(selectedId) as BizEntityTypeCategory;
      }
    }
    
    // Link is already updated via onLinkTypeChange, but ensure it's set
    // (selectedLinkType getter returns the current value from _config)
    // console.log({
    //   data: this._config,
    //   isRelationConfigValid: this.isFormValid(this._config)
    // })
    return {
      data: this._config,
      isRelationConfigValid: this.isFormValid(this._config)
    };
  }

  // Polymorphic control meta objects for form fields
  polyTargetControl!: IPolymorphicControlMeta;
  polyRelationshipTypeControl!: IPolymorphicControlMeta;
  polyMinimumControl!: IPolymorphicControlMeta;
  polyMaximumControl!: IPolymorphicControlMeta;
  polyTabTitleControl!: IPolymorphicControlMeta;

  // Options for radio buttons (manual implementation)
  targetOptions = [
    { id: RelationshipTargetType.BizEntityType, label: 'Business Entity Type' },
    { id: RelationshipTargetType.BizEntityTypeCategory, label: 'Category' }
  ];

  // Expose enum for template usage
  RelationshipLinkType = RelationshipLinkType;
  
  // Get all link type options from enum
  get linkTypeOptions(): RelationshipLinkType[] {
    return Object.values(RelationshipLinkType).filter(v => typeof v === 'number') as RelationshipLinkType[];
  }

  private mappedBizEntityTypes: { id: string; label: string; icon: string }[] = [];
  private mappedBizEntityTypeCategories: { id: string; label: string; icon: string }[] = [];

  private readonly polymorphicControlService = inject(PolymorphicControlService);


  constructor() {
  }

  ngOnInit(): void {
    // Handle array case - extract first element if it's an array
    this._config = Array.isArray(this.relationshipConfig) 
      ? this.relationshipConfig[0] 
      : this.relationshipConfig;
    
    // Ensure targetType is set (default to BizEntityType)
    if (this._config.targetType === undefined || this._config.targetType === null) {
      this._config.targetType = RelationshipTargetType.BizEntityType;
    }
    
    this.prepareBizEntityTypes();
    this.buildPolymorphicControls();
    this.populateAppTabConfigForCrossAppRelationship();
    // console.log('Initial relationship config:', this._config);
  }
 

  private buildPolymorphicControls(): void {
    this.polyTargetControl = this.polymorphicControlService.getC({
      id: 'target',
      type: PolyMorphicControlTypes.options,
      label: 'Select Target',
      icon: 'cilList',
      optionsWithIcon: this._config?.targetType === RelationshipTargetType.BizEntityType
        ? this.mappedBizEntityTypes
        : this.mappedBizEntityTypeCategories,
      value: this.getTargetId(),
      optionSelected: this.getTargetName(),
      optionHeader: 'Select Target',
      optionPlaceholder: 'Choose a target',
      floatingLabel: true,
      fieldMeta: {
        id: 'target',
        name: 'Target',
        type: FieldTypes.text,
        label: ' Target Field',
        isRequired: true,
      },
      containerClass: 'target-control-container',
      optionChangeCallback: (value: {id: string, icon: string, label: string}) => {
        this.onTargetChange(value);
      }
    });

    // Relationship Type Control (Compositional/Referential)
    this.polyRelationshipTypeControl = this.polymorphicControlService.getC({
      id: 'relationship-type',
      type: PolyMorphicControlTypes.options,
      label: 'Select Relationship Type',
      icon: 'cilLink',
      optionsWithIcon: [
        { id: RelationshipType.Referential.toString(), label: 'Referential', icon: '' },
        { id: RelationshipType.Compositional.toString(), label: 'Compositional', icon: '' }
      ],
      value: this.getRelationshipTypeLabel(),
      optionSelected: this.getRelationshipTypeLabel(),
      optionHeader: 'Select Relationship Type',
      optionPlaceholder: 'Choose a relationship type',
      floatingLabel: true,
      fieldMeta: {
        id: 'relationship-type',
        name: 'Relationship Type',
        type: FieldTypes.text,
        label: 'Relationship Type Field',
        isRequired: true,
      },
      containerClass: 'relationship-type-control-container',
      optionChangeCallback: (value: {id: string, icon: string, label: string}) => {
        this.onRelationshipTypeChange(parseInt(value.id) as RelationshipType);
      }
    });

    // Minimum Cardinality
    this.polyMinimumControl = this.polymorphicControlService.getC({
      id: 'minimum',
      type: PolyMorphicControlTypes.number,
      label: 'Minimum',
      icon: 'cilCalculator',
      value: this._config?.cardinalityMin || 0,
      floatingLabel: true,
      disabled: this.isEditing,
      containerClass: 'minimum-control-container',
      fieldMeta: {
        id: 'minimum',
        name: 'Minimum',
        min: 0,
        type: FieldTypes.number,
        label: ' Minimum Field',
        isRequired: true,
      },
    });

    // Maximum Cardinality
    this.polyMaximumControl = this.polymorphicControlService.getC({
      id: 'maximum',
      type: PolyMorphicControlTypes.number,
      label: 'Maximum',
      icon: 'cilCalculator',
      value: this._config?.cardinalityMax || 1,
      floatingLabel: true,
      disabled: this.isEditing,
      containerClass: 'maximum-control-container',
      fieldMeta: {
        id: 'maximum',
        name: 'Maximum',
        min: 1,
        type: FieldTypes.number,
        label: ' Maximum Field',
        isRequired: true,
      },
    });

    // Tab Title
    this.polyTabTitleControl = this.polymorphicControlService.getC({
      id: 'tab-title',
      type: PolyMorphicControlTypes.text,
      label: 'Tab Title',
      icon: 'cilCalculator',
      value: this._config?.view?.tabTitle || '',
      floatingLabel: true,
      fieldMeta: {
        id: 'tab-title',
        name: 'Tab Title',
        type: FieldTypes.text,
        label: ' Tab Title Field',
        isRequired: true,
        inputPattern: '^\\S.{0,48}\\S$|^\\S{2}$'
      },
      customValidationMessages: {
        pattern: "Tab Title must be 2-50 characters. Leading and trailing spaces are not allowed.",
      }, 
    });
    
  }

  private prepareBizEntityTypes(): void {

        this.mappedBizEntityTypes = this.bizEntityTypes.map(type => ({
          id: type.id?.toString() || '',
          label: type.name,
          icon: type.icon || '',
        }));

        // Map BizEntityTypeCategory enum to options
        this.mappedBizEntityTypeCategories = [
          { id: BizEntityTypeCategory.Structural.toString(), label: 'Structural', icon: 'cilLayers' },
          { id: BizEntityTypeCategory.BenzaitenObjective.toString(), label: 'Objective', icon: 'cilFlag' },
          { id: BizEntityTypeCategory.BenzaitenDeliverable.toString(), label: 'Deliverable', icon: 'cilTask' },
          { id: BizEntityTypeCategory.BenzaitenKPI.toString(), label: 'KPI', icon: 'cilSpeedometer' },
          { id: BizEntityTypeCategory.BenzaitenMeasure.toString(), label: 'Measure', icon: 'cilChartLine' },
        ];
  }

  private getTargetId(): string {
    if (!this._config?.target) {
      return '';
    }
    if (this._config.targetType === RelationshipTargetType.BizEntityType) {
      const target = this._config.target as IBizEntityType;
      return target?.id?.toString() || '';
    } else {
      // For BizEntityTypeCategory, return the enum value as string
      return (this._config.target as BizEntityTypeCategory).toString();
    }
  }

  private getTargetName(): string {
    if (!this._config?.target) {
      return '';
    }
    if (this._config.targetType === RelationshipTargetType.BizEntityType) {
      const target = this._config.target as IBizEntityType;
      return target?.name || '';
    } else {
      // For BizEntityTypeCategory, return the label from mapped categories
      const categoryId = (this._config.target as BizEntityTypeCategory).toString();
      const matchedCategory = this.mappedBizEntityTypeCategories.find(cat => cat.id === categoryId);
      return matchedCategory?.label || '';
    }
  }

  private getRelationshipTypeLabel(): string {
    if (!this._config?.relationshipType) {
      return 'Referential';
    }
    
    switch (this._config.relationshipType) {
      case RelationshipType.Compositional:
        return 'Compositional';
      case RelationshipType.Referential:
        return 'Referential';
      default:
        return 'Referential';
    }
  }

  onCreateRelationshipConfiguration(): void {
    // All values are already updated in _config via onValueChange handlers
    if (this.isEditing) {
     
    } else {

    }
    
    // TODO: Call state service to save the configuration
  }

  onCreateNewFieldClicked(): void {
    
  }

  onTargetTypeChange(targetType: RelationshipTargetType): void {
    this._config.targetType = targetType;
    this.polyTargetControl.optionsWithIcon = targetType === RelationshipTargetType.BizEntityType
      ? this.mappedBizEntityTypes
      : this.mappedBizEntityTypeCategories;
    this.polyTargetControl.optionSelected = '';
  }

  onTargetChange(value: {id: string, label: string , icon: string}): void {
    let category: BizEntityTypeCategory | null = null;
    
    // Default to BizEntityType if targetType is not set
    const targetType = this._config.targetType ?? RelationshipTargetType.BizEntityType;
    
    if (targetType === RelationshipTargetType.BizEntityType) {
      // First try to find in the active tab's cache
      let selectedType = this.appsBizEntityTypes[this.xAppCapTabConfig.activeTabId]?.find(t => t.id.toString() === value.id);
      
      // If not found in active tab, search across ALL cached apps
      if (!selectedType) {
        for (const appId of Object.keys(this.appsBizEntityTypes)) {
          selectedType = this.appsBizEntityTypes[appId]?.find(t => t.id.toString() === value.id);
          if (selectedType) {
            break;
          }
        }
      }
      
      this.polyTargetControl.value = value.id;
      this.polyTargetControl.optionSelected = value.label;
      if (selectedType) {
        this._config.target = selectedType; // Set target immediately
        category = selectedType.category;
      }
    } else if (targetType === RelationshipTargetType.BizEntityTypeCategory) {
      category = parseInt(value.id) as BizEntityTypeCategory;
      this._config.target = category; // Set target immediately
      this.polyTargetControl.value = value.id;
      this.polyTargetControl.optionSelected = value.label;
    }
    if (category !== null) {
      this.applyCardinalityRules(category);
    }
  }

  private applyCardinalityRules(category: BizEntityTypeCategory): void {

    if (category === BizEntityTypeCategory.Transactional) {
      this._config.link = RelationshipLinkType.Tight;
      this._config.cardinalityMin = 1;
      this._config.cardinalityMax = 1;
  
      this.polyMinimumControl.value = '1';
      this.polyMinimumControl.disabled = true;
      this.polyMaximumControl.value = '1';
      this.polyMaximumControl.disabled = true;
    } else {
      // Referential: Loose link, editable cardinality
      this._config.link = RelationshipLinkType.Loose;
      
      // Enable cardinality controls (unless in edit mode)
      this.polyMinimumControl.disabled = this.isEditing;
      this.polyMaximumControl.disabled = this.isEditing;
    }
  }

  onLinkTypeChange(linkType: RelationshipLinkType): void {
    this._config.link = linkType;
  }

  onRelationshipTypeChange(relationshipType: RelationshipType): void {
    this._config.relationshipType = relationshipType;
  }

  private checkCardinalityValidation(): void {
    const min = this._config.cardinalityMin;
    const max = this._config.cardinalityMax;

    // Reset error state
    this.isCardinalityInvalid = false;
    this.cardinalityErrorMessage = '';

    if ( max > 1 && (max < min)) {
        this.isCardinalityInvalid = true;
        this.cardinalityErrorMessage = 'Maximum cardinality must be greater than or equal to minimum cardinality.';
      }
  }

  get selectedTargetType(): RelationshipTargetType {
    return this._config?.targetType || RelationshipTargetType.BizEntityType;
  }

  get selectedLinkType(): RelationshipLinkType {
    return this._config?.link || RelationshipLinkType.Tight;
  }
  
  get isLinkDisabled(): boolean {
    // Link is disabled in edit mode OR when target is transactional
    if (this.isEditing) return true;
    
    const selectedId = this.polyTargetControl.value;
    if (!selectedId) return false;
    
    let category: BizEntityTypeCategory | null = null;
    const targetType = this._config.targetType ?? RelationshipTargetType.BizEntityType;
    
    if (targetType === RelationshipTargetType.BizEntityType) {
      // Search across all cached apps
      let selectedType: IBizEntityType | undefined;
      for (const appId of Object.keys(this.appsBizEntityTypes)) {
        selectedType = this.appsBizEntityTypes[appId]?.find(t => t.id.toString() === selectedId);
        if (selectedType) {
          category = selectedType.category;
          break;
        }
      }
    } else {
      const categoryId = parseInt(selectedId);
      category = categoryId as BizEntityTypeCategory;
    }
    
    // Link is disabled if category is Transactional
    return category === BizEntityTypeCategory.Transactional;
  }

  get isRelationshipTypeVisible(): boolean {
    // Relationship type is visible only when target is non-transactional
    const selectedId = this.polyTargetControl?.value;
    if (!selectedId) return false;
    
    let category: BizEntityTypeCategory | null = null;
    const targetType = this._config.targetType ?? RelationshipTargetType.BizEntityType;
    
    if (targetType === RelationshipTargetType.BizEntityType) {
      // Search across all cached apps
      let selectedType: IBizEntityType | undefined;
      for (const appId of Object.keys(this.appsBizEntityTypes)) {
        selectedType = this.appsBizEntityTypes[appId]?.find(t => t.id.toString() === selectedId);
        if (selectedType) {
          category = selectedType.category;
          break;
        }
      }
    } else {
      const categoryId = parseInt(selectedId);
      category = categoryId as BizEntityTypeCategory;
    }
    
    // Show relationship type only for non-transactional categories
    return category !== null && category !== BizEntityTypeCategory.Transactional;
  }

  /**
   * Validates the relationship configuration form
   * @param data - The relationship configuration to validate
   * @returns true if the form is valid, false otherwise
   */
  isFormValid = (data: IRelationshipConfiguration = this._config): boolean => {
    // 1. Check if targetType is defined
    if (data.targetType === undefined || data.targetType === null) {
      return false;
    }

    // 2. Check if target is defined
    if (!data.target) {
      return false;
    }

    // 3. Validate target based on targetType
    if (data.targetType === RelationshipTargetType.BizEntityType) {
      const targetEntity = data.target as IBizEntityType;
      if (!targetEntity.id || !targetEntity.name) {
        return false;
      }
    } else if (data.targetType === RelationshipTargetType.BizEntityTypeCategory) {
      // Validate category enum value
      const categoryValue = data.target as BizEntityTypeCategory;
      if (categoryValue === undefined || categoryValue === null) {
        return false;
      }
    }

    // 4. Check if view tabTitle is defined and valid (2-50 characters, no leading/trailing spaces)
    if (!data.view?.tabTitle || data.view.tabTitle.trim() === '') {
      return false;
    }

    const tabTitlePattern = /^\S.{0,48}\S$|^\S{2}$/;
    if (!tabTitlePattern.test(data.view.tabTitle)) {
      return false;
    }

    // 5. Validate cardinality - check if defined
    if (data.cardinalityMin === undefined || data.cardinalityMin === null) {
      return false;
    }

    if (data.cardinalityMax === undefined || data.cardinalityMax === null) {
      return false;
    }

    // 6. Min must be >= 0
    if (data.cardinalityMin < 0) {
      return false;
    }

    // 7. Max must be >= 1
    if (data.cardinalityMax < 1) {
      return false;
    }

    // 8. Max must be >= min
    if (data.cardinalityMax < data.cardinalityMin) {
      return false;
    }

    // 9. Check if relationshipType is defined
    if (data.relationshipType === undefined || data.relationshipType === null) {
      return false;
    }

    // 10. Check if link type is defined
    if (data.link === undefined || data.link === null) {
      return false;
    }

    // 11. Check for custom cardinality validation errors
    if (this.isCardinalityInvalid) {
      return false;
    }

    return true;
  };

  populateAppTabConfigForCrossAppRelationship() {
    this.appFrameworkContextService.currentApp$.subscribe(app => {
      if(app) {
        this.currentAppId = app.id;
      }
    });

    this.appFrameworkContextService.availableApps$.subscribe(apps => {
      this.availableApps = apps;
    });

    let tabs: ITab[] = [];
    tabs = this.availableApps.map((app, index) => ({
      id: app.id,
      title: app.name,
      order: this.currentAppId && app.id === this.currentAppId ? 0 : index + 1
    }));
    this.xAppCapTabConfig.tabs = tabs;
    this.xAppCapTabConfig.activeTabId = this.currentAppId ? this.currentAppId : (tabs[0]?.id || '');
    
    // Check if we have an existing target to patch
    if (this._config?.target && this._config.targetType === RelationshipTargetType.BizEntityType) {
      const target = this._config.target as IBizEntityType;
      // console.log('Existing target found in config:', this._config);
      if (target.id && target.appId) {
        // Set the active tab to the target's app
        this.xAppCapTabConfig.activeTabId = target.appId;
        // Load biz entity types for the target's app and patch
        this.loadAndPatchTarget(target.appId, target.id.toString(), target.name);
        return;
      }
    }
    
    // Load biz entity types for the initially active app
    if (this.xAppCapTabConfig.activeTabId) {
      this.populateRespetiveTargetsForSelectedApp(this.xAppCapTabConfig.activeTabId);
    }
  }

  private loadAndPatchTarget(appId: string, targetId: string, targetName: string): void {
    const targets = this.fetchBizEntityTypesForSelectedApp(appId);
    // console.log('Loading biz entity types for appId', appId, targets);
    if (targets) {
      targets.then(types => {
        if (types) {
          // types = types.filter(t => t.isSystemBasic === false); // Only include non-system types
          types = types.filter(t => !t.isSystemBasic && !BizEntityTypeCategoriesNotAvailableForSelection.includes(t.category)); // Only include non-system types
          // Cache the fetched types
          this.appsBizEntityTypes[appId] = types;
          // console.log('Fetched biz entity types for appId', appId, types);
          // Map to options format
          this.mappedBizEntityTypes = types.map(type => ({
            id: type.id?.toString() || '',
            label: type.name,
            icon: type.icon || '',
          }));
          
          // Update the polyTargetControl options
          if (this._config?.targetType === RelationshipTargetType.BizEntityType) {
            this.polyTargetControl.optionsWithIcon = this.mappedBizEntityTypes;
          }
          
          // Find the target entity and patch the control
          const targetEntity = types.find(t => t.id.toString() === targetId);
          if (targetEntity) {
            this.polyTargetControl.value = targetEntity.id.toString();
            this.polyTargetControl.optionSelected = targetEntity.name;
          } else {
            // Fallback: use the provided name if entity not found
            this.polyTargetControl.value = targetId;
            this.polyTargetControl.optionSelected = targetName;
          }
        }
      }).catch(error => {
        console.error('Error fetching biz entity types for target app:', error);
      });
    }
  }

  onAppTabChanged(tabId:string): void {
    this.xAppCapTabConfig.activeTabId = tabId;
    const appId = tabId;
    this.populateRespetiveTargetsForSelectedApp(appId);
  }

  private populateRespetiveTargetsForSelectedApp(appId: string): void {
    this.polyTargetControl.optionSelected = ''; // Reset selection when switching apps
    const targets = this.fetchBizEntityTypesForSelectedApp(appId);
    if (targets) {
      targets.then(types => {
        if (types) {
          //TODO remove hardcoded filtering once we have a better way to identify which system types should be excluded
          // Should be resolved ASAP as it impacts the user experience by showing irrelevant system types in the target selection dropdown for cross-app relationships
          // console.log('Fetched biz entity types for appId', appId, types);
            types = types.filter(t => !t.isSystemBasic && !BizEntityTypeCategoriesNotAvailableForSelection.includes(t.category));
          // Cache the fetched types
          this.appsBizEntityTypes[appId] = types;
          
          // Map to options format
          this.mappedBizEntityTypes = types.map(type => ({
            id: type.id?.toString() || '',
            label: type.name,
            icon: type.icon || '',
          }));
          
          // Update the polyTargetControl options if target type is BizEntityType
          if (this._config?.targetType === RelationshipTargetType.BizEntityType) {
            this.polyTargetControl.optionsWithIcon = this.mappedBizEntityTypes;
          }
        }
      }).catch(error => {
        console.error('Error fetching biz entity types for app:', error);
      });
    }
  }

  fetchBizEntityTypesForSelectedApp(appId: string): Promise<IBizEntityType[] | undefined> {
    let xAppCappParam : IXappCap | undefined;

    if(!appId || (this.currentAppId && this.currentAppId === appId)){
      xAppCappParam = undefined; //fetch for current app without passing xAppCap param
    } else{
      xAppCappParam = { appId: appId }; //fetch for other app by passing xAppCap param
    }

    if (this.appsBizEntityTypes[appId]) {
      return Promise.resolve(this.appsBizEntityTypes[appId]);
    } else {
      const targets = this.bizEntityTypeStateStoreService.getState(undefined, xAppCappParam)
      return targets;
    }
  }

}
